import ApiService from './api';
import { supabase } from '../lib/supabase';

export interface CreditBalance {
  credits: number;
}

export interface Package {
  id: string;
  name: string;
  credits: number;
  priceUsd: number;
  pricePerCredit: number;
}

export class PaymentsService {
  // Cache for credits to reduce API calls
  private static cachedCredits: { data: CreditBalance; timestamp: number } | null = null;
  private static CACHE_DURATION = 5000; // 5 seconds cache

  /**
   * Get user's current credit balance
   * Uses a 5-second cache to improve performance
   */
  static async getCredits(skipCache: boolean = false): Promise<CreditBalance> {
    // Return cached data if available and fresh
    if (!skipCache && this.cachedCredits) {
      const age = Date.now() - this.cachedCredits.timestamp;
      if (age < this.CACHE_DURATION) {
        return this.cachedCredits.data;
      }
    }

    const response = await fetch(`${process.env.REACT_APP_API_URL}/credits`, {
      headers: {
        'Authorization': `Bearer ${await this.getToken()}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch credits');
    }

    const data = await response.json();

    // Update cache
    this.cachedCredits = {
      data: data.data,
      timestamp: Date.now()
    };

    return data.data;
  }

  /**
   * Invalidate the credits cache (call after purchase or processing)
   */
  static invalidateCache(): void {
    this.cachedCredits = null;
  }

  /**
   * Get available credit packages
   */
  static async getPackages(): Promise<Package[]> {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/packages`);

    if (!response.ok) {
      throw new Error('Failed to fetch packages');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Create a Stripe checkout session and redirect to checkout
   */
  static async purchasePackage(packageId: string): Promise<void> {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await this.getToken()}`
      },
      body: JSON.stringify({ packageId })
    });

    if (!response.ok) {
      throw new Error('Failed to create checkout session');
    }

    const data = await response.json();
    const checkoutUrl = data.data.checkoutUrl;

    // Redirect to Stripe checkout
    window.location.href = checkoutUrl;
  }

  /**
   * Get Supabase auth token (optimized - no dynamic import)
   */
  private static async getToken(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || '';
  }
}

export default PaymentsService;
